import moment from 'moment'
import { useEffect, useState } from 'react'
import {
  PriceComponentAPI,
  PriceComponentTypeModel
} from '../../../api/Argentia/endpoints/priceComponents'
import { PriceValuesModel } from '../../../api/Argentia/endpoints/pricing/model/values'
import { fetchPriceByID } from '../../../api/Argentia/endpoints/pricing/pricing.api'
import { EnumPriceType } from '../../../api/oData/enums/enumPriceType'
import { HttpErrorProps } from '../../../hooks'
import { ErrorCodes } from '../../../model'
import { fetchPriceCurrentValues } from '../../../pages/pricing/pricing.service'
import {
  priceComponentsAPIItemMapper,
  priceComponentsAPIListMapper,
  priceComponentsDefaultAPIListMapper,
  priceMapper
} from './priceComponentsTable.mapper'
import {
  getPriceComponent,
  getPriceComponentDefaults,
  getPriceComponents,
  getPriceComponentTypes
} from './priceComponentsTable.service'
import { PriceModel } from './priceComponentsTable.types'

interface UsePriceComponents {
  priceComponents: PriceComponentAPI[]
  priceComponent: PriceComponentAPI | null
  priceComponentsDefault: PriceComponentAPI[]
  price: PriceModel | null
  defaultPriceValue: PriceValuesModel | null
  isFetchingPriceComponentsDefault: boolean
  isFetchingPriceComponents: boolean
  isFetchingPriceComponent: boolean
  isFetchingPrice: boolean
  isFetchingPriceValues: boolean
  priceValueIsNull?: boolean
  errorFetchPriceComponents: HttpErrorProps | null
  errorFetchPriceComponentsDefaults: HttpErrorProps | null
  getPriceForPriceComponent: (priceComponentID: number) => void
  getPriceForPriceComponentDefault: (priceComponentID: number) => Promise<PriceModel | null>
  getComponents: (contractId?: number, flexibilityId?: number, leadId?: number) => Promise<void>
  refreshPriceComponents: () => Promise<void>
}

interface UsePriceComponentsProps {
  priceComponentID?: number
  contractID?: number
  flexibilityID?: number
  leadID?: number
  referenceType?: number
  priceType?: number
  commodity?: number
  idDummyPrice?: number
  priceComponentTypeID?: number
  preventDefault?: boolean
}

export default function usePriceComponents({
  priceComponentID,
  contractID,
  flexibilityID,
  leadID,
  referenceType,
  priceType,
  commodity,
  idDummyPrice,
  priceComponentTypeID,
  preventDefault = false
}: UsePriceComponentsProps): UsePriceComponents {
  const [isFetchingPriceComponents, setIsFetchingPriceComponents] = useState<boolean>(false)
  const [isFetchingPriceComponent, setIsFetchingPriceComponent] = useState<boolean>(false)
  const [isFetchingPriceComponentsDefault, setIsFetchingPriceComponentsDefault] =
    useState<boolean>(false)
  const [isFetchingPrice, setIsFetchingPrice] = useState<boolean>(false)
  const [isFetchingPriceValues, setIsFetchingPriceValues] = useState<boolean>(false)

  const [priceComponents, setPriceComponents] = useState<PriceComponentAPI[]>([])
  const [priceComponentsDefault, setPriceComponentsDefault] = useState<PriceComponentAPI[]>([])
  const [priceComponent, setPriceComponent] = useState<PriceComponentAPI | null>(null)
  const [price, setPrice] = useState<PriceModel | null>(null)
  const [defaultPriceValue, setDefaultPriceValue] = useState<PriceValuesModel | null>(null)
  const [errorFetchPriceComponents, setErrorFetchPriceComponents] = useState<HttpErrorProps | null>(
    null
  )
  const [errorFetchPriceComponent, setErrorFetchPriceComponent] = useState<HttpErrorProps | null>(
    null
  )
  const [errorFetchPriceComponentsDefaults, setErrorFetchPriceComponentsDefaults] =
    useState<HttpErrorProps | null>(null)
  const [priceValueIsNull, setPriceValueIsNull] = useState<boolean>(false)

  const getComponents = async (contractId?: number, flexibilityId?: number, leadId?: number) => {
    let result: PriceComponentAPI[] | undefined
    result = await fetchPriceComponents(contractId, flexibilityId, leadId)
    setPriceComponents(priceComponentsAPIListMapper(result ?? []))
  }

  const getComponent = async (componentID: number) => {
    let result: PriceComponentAPI | null
    result = await fetchPriceComponent(componentID)
    setPriceComponent(result ? priceComponentsAPIItemMapper(result) : null)
    return result
  }

  const getComponentDefaults = async (
    referenceType?: number,
    priceType?: number,
    commodity?: number
  ) => {
    let result: PriceComponentAPI[] | undefined
    result = await fetchPriceComponentDefaults(referenceType, priceType, commodity)
    setPriceComponentsDefault(priceComponentsDefaultAPIListMapper(result ?? []))
    return result
  }

  const fetchPriceComponents = async (
    contractId?: number,
    flexibilityId?: number,
    leadId?: number
  ): Promise<PriceComponentAPI[] | undefined> => {
    setIsFetchingPriceComponents(true)
    setPriceComponents([])
    setErrorFetchPriceComponents(null)

    return await getPriceComponents({ contractId, flexibilityId, leadId }).then(response => {
      setIsFetchingPriceComponents(false)
      if (response.success) {
        if (response.data !== undefined) {
          return response.data
        } else {
          return []
        }
      } else {
        response.error &&
          setErrorFetchPriceComponents({
            errorCode: ErrorCodes.SERVER_ERROR,
            onRefresh: async () => {
              getComponents(contractId, flexibilityId, leadId)
            }
          })
        return []
      }
    })
  }

  const fetchPriceComponent = async (id: number): Promise<PriceComponentAPI | null> => {
    setIsFetchingPriceComponent(true)
    setPriceComponent(null)
    setErrorFetchPriceComponent(null)

    return await getPriceComponent({ id }).then(response => {
      setIsFetchingPriceComponent(false)
      if (response.success) {
        if (response.data !== undefined) {
          return response.data
        } else {
          return null
        }
      } else {
        response.error &&
          setErrorFetchPriceComponent({
            errorCode: ErrorCodes.SERVER_ERROR,
            onRefresh: async () => {
              getComponent(id)
            }
          })
        return null
      }
    })
  }

  const fetchPriceComponentDefaults = async (
    referenceType?: number,
    priceType?: number,
    commodity?: number
  ): Promise<PriceComponentAPI[] | undefined> => {
    setIsFetchingPriceComponentsDefault(true)
    setPriceComponentsDefault([])
    setErrorFetchPriceComponents(null)

    return await getPriceComponentDefaults(referenceType, priceType, commodity).then(response => {
      setIsFetchingPriceComponentsDefault(false)
      if (response.success) {
        if (response.data !== undefined) {
          return response.data
        } else {
          return []
        }
      } else {
        response.error &&
          setErrorFetchPriceComponentsDefaults({
            errorCode: ErrorCodes.SERVER_ERROR,
            onRefresh: async () => {
              getComponentDefaults(referenceType, priceType, commodity)
            }
          })
        return []
      }
    })
  }

  const getPriceForPriceComponent = async (priceComponentID: number) => {
    const priceComponentPriceID = priceComponents.find(p => p.id === priceComponentID)?.idPrice
    if (priceComponentPriceID) {
      getPriceByID(priceComponentPriceID)
    }
    return null
  }

  const getPriceComponentFromPriceComponentArray = (
    priceComponentID: number,
    priceComponents: PriceComponentAPI[]
  ) => {
    const priceComponent = priceComponents.find(p => p.id === priceComponentID)
    return priceComponent ? priceComponent : null
  }

  const getPriceComponentFromPriceComponentTypeID = (
    priceComponentTypeID: number,
    priceComponents: PriceComponentAPI[]
  ) => {
    const priceComponent = priceComponents.find(
      p => p.idPriceComponentType === priceComponentTypeID
    )
    return priceComponent ? priceComponent : null
  }

  const getPriceValueFromPriceValuesArray = (priceID: number, priceValues: PriceValuesModel[]) => {
    const priceValue = priceValues.find(pv => pv.priceId === priceID)
    return priceValue ? priceValue : null
  }

  const getPriceForPriceComponentDefault = async (
    priceComponentID: number
  ): Promise<PriceModel | null> => {
    const priceComponentDefaultSelected = getPriceComponentFromPriceComponentArray(
      priceComponentID,
      priceComponentsDefault
    )
    const defaultPriceID = priceComponentDefaultSelected?.idPrice
    if (defaultPriceID) {
      setIsFetchingPrice(true)
      const price = await fetchPriceByID(defaultPriceID)
      getPriceCurrentValuesByIDs(defaultPriceID, null) // to set defaultPriceValue
      setIsFetchingPrice(false)
      return priceMapper(price)
    }
    return null
  }

  const getPriceByID = async (id: number) => {
    setIsFetchingPrice(true)
    const price = await fetchPriceByID(id)
    const mappedPrice: PriceModel = {
      ...priceMapper(price)
    }
    setPrice(mappedPrice)
    setIsFetchingPrice(false)
  }

  const getPriceCurrentValuesByIDs = async (
    defaultPriceID: number | null,
    priceID: number | undefined | null
  ) => {
    const pricesIds: number[] = []
    defaultPriceID && pricesIds.push(defaultPriceID)
    priceID && pricesIds.push(priceID)

    if (pricesIds.length) {
      setIsFetchingPriceValues(true)

      const today = moment().format('YYYY-MM-DD')

      const priceCurrentValuesResponse = await fetchPriceCurrentValues(
        pricesIds,
        today,
        today
      ).finally(() => {
        setIsFetchingPriceValues(false)
      })

      if (priceCurrentValuesResponse.success && priceCurrentValuesResponse.data) {
        const priceCurrentValues = priceCurrentValuesResponse.data

        defaultPriceID &&
          setDefaultPriceValue(
            getPriceValueFromPriceValuesArray(defaultPriceID, priceCurrentValues) ?? null
          )

        if (priceID) {
          const priceValue = getPriceValueFromPriceValuesArray(priceID, priceCurrentValues)
          if (priceValue?.value === null || priceValue?.value === undefined) {
            setPriceValueIsNull(true)
          } else {
            setPriceValueIsNull(false)
          }
          setPrice(prevState => {
            if (!prevState) return prevState
            return {
              ...prevState,
              value: getPriceValueFromPriceValuesArray(priceID, priceCurrentValues)?.value ?? null
            }
          })
        }
      }
    }
  }

  useEffect(() => {
    const fetchData = async () => {
      const promisesArray: Promise<any>[] = []

      if (!preventDefault) {
        // preventDefault prevents fetching componentsDefault when loading PriceComponentsTable DataTable
        promisesArray.push(getPriceComponentTypes(referenceType, priceType, commodity, true))
      } else {
        promisesArray.push(Promise.resolve([]))
      }
      if (priceComponentID) {
        // priceComponent is already selected (ADD/EDIT Form in PriceComponentInstancesPage useCase)
        promisesArray.push(getComponent(priceComponentID))
      } else if (contractID || flexibilityID || leadID) {
        // gets list of priceComponents already associated with contractID, flexibilityID or leadID for PriceComponentTable fill-in dataTable useCase
        // for ADD/EDIT PriceComponentsAddPage, this information is sent through priceComponentToExclude prop
        promisesArray.push(getComponents(contractID, flexibilityID, leadID))
      }

      await Promise.all(promisesArray).then(responses => {
        const priceComponentsDefaultResponse = responses[0] as PriceComponentAPI[] | undefined
        const priceComponentResponse = priceComponentID
          ? (responses[1] as PriceComponentAPI | null)
          : null

        const defaultPriceComponentTypeID = priceComponentTypeID
          ? priceComponentTypeID // // gets defaultPriceValue in priceComponentInstance ADD Form useCase
          : (priceComponentResponse?.idPriceComponentType ?? null) // gets defaultPriceValue in priceComponent EDIT Form useCase

        const defaultPriceID =
          defaultPriceComponentTypeID &&
          (getPriceComponentFromPriceComponentTypeID(
            defaultPriceComponentTypeID,
            priceComponentsDefaultResponse ?? []
          )?.idPrice ??
            null)

        const priceComponentPriceID = priceComponentResponse?.idPrice

        if (priceComponentPriceID) {
          getPriceByID(priceComponentPriceID) // gets Form precioUnitario and redondeo in priceComponent EDIT Form useCase
        } else if (idDummyPrice) {
          getPriceByID(idDummyPrice) // gets Form precioUnitario and redondeo in priceComponentInstance ADD Form useCase
        }
        getPriceCurrentValuesByIDs(
          defaultPriceID,
          priceType === EnumPriceType.Parametro ? priceComponentPriceID : null // apuntamiento o parametroPeriodo has no Value but a periodValues(parametroPeriodo) or coefficients(apuntamiento) lists
        ) // gets Form defaultValue and value inputs
      })
    }

    fetchData()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    priceComponentID,
    idDummyPrice,
    priceComponentTypeID,
    priceType,
    contractID,
    flexibilityID,
    leadID,
    preventDefault
  ])

  return {
    priceComponents,
    priceComponent,
    priceComponentsDefault,
    price,
    defaultPriceValue,
    isFetchingPriceComponentsDefault,
    isFetchingPriceComponents,
    isFetchingPriceComponent,
    isFetchingPrice,
    isFetchingPriceValues,
    priceValueIsNull,
    getPriceForPriceComponent,
    getPriceForPriceComponentDefault,
    errorFetchPriceComponents,
    errorFetchPriceComponentsDefaults,
    getComponents,
    refreshPriceComponents: () => getComponents(contractID, flexibilityID, leadID)
  }
}
